<template>
  <div class="search-page__wrapper">
    <div class="search-page__inner">
      <h1 class="search-page__headline headline-large">
        {{ $t('search.title') }}
      </h1>
      <div class="search-page__bg-image"></div>
      <section
        v-if="isAutoSubmitting"
        class="loading-container search-page page-wrapper"
      >
        <Spinner size="large" />
        <p>{{ $t('search.loading') }}</p>
      </section>
      <section
        v-else
        class="search-page page-wrapper"
      >
        <section class="search-container">
          <SearchOrigin />
          <SearchDestination />
          <DirectionButton />
        </section>
        <div class="search-page__calendar-desktop">
          <TravelDate />
        </div>
        <div class="search-page__calendar-desktop">
          <ReturnDate />
        </div>
        <div class="search-page__calendar-mobile">
          <TravelDate />
          <ReturnDate />
        </div>
        <router-link
          class="button button--medium button--primary"
          :class="{ disabled: !canProceed }"
          :to="toLink"
        >
          {{ $t('search.continue') }}
        </router-link>
      </section>
    </div>
  </div>
</template>

<script lang="ts">
  import {
    defineComponent,
    computed,
    ref,
    onMounted,
    watch,
    nextTick,
  } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { useJourneyStore } from '@/stores/journeyStore'
  import { useBookingStore } from '@/stores/bookingStore'
  import { useSeatReservationStore } from '@/stores/seatReservationStore'
  import { useMyTravelStore } from '@/stores/myTravelStore'
  import { useUserStore } from '@/stores/userStore'
  import { useErrorStore } from '@/stores/errorStore'
  import { getToken } from '@/services/tokenService'
  import { fetchAllStations } from '@/services/journeyService'
  import { cancelProvisionalBooking } from '../../services/journeyService'
  import { getStoredProvisionalBookingNumber, clearProvisionalBookingInfo } from '../../services/storageService'
  import Spinner from '@/components/BaseComponents/Spinner/Spinner.vue'
  import SearchOrigin from '@/components/TravelPlanner/SearchOrigin/SearchOrigin.vue'
  import SearchDestination from '@/components/TravelPlanner/SearchDestination/SearchDestination.vue'
  import TravelDate from '@/components/TravelPlanner/Traveldate/TravelDate.vue'
  import ReturnDate from '@/components/TravelPlanner/ReturnDate/ReturnDate.vue'
  import DirectionButton from '@/components/TravelPlanner/DirectionButton.vue'
  import { isValid } from 'date-fns'

  export default defineComponent({
    name: 'Search',
    components: {
      SearchOrigin,
      DirectionButton,
      SearchDestination,
      TravelDate,
      ReturnDate,
      Spinner,
    },
    setup() {
      const route = useRoute()
      const router = useRouter()
      const journeyStore = useJourneyStore()
      const bookingStore = useBookingStore()
      const seatReservationStore = useSeatReservationStore()
      const myTravelStore = useMyTravelStore()
      const userStore = useUserStore()
      const isAutoSubmitting = ref(false)
      const isWatching = ref(true)

      const canProceed = computed(() => {
        return (
          journeyStore.OriginStation?.name &&
          journeyStore.destinationStation?.name &&
          journeyStore.departureDate &&
          isFormValid.value
        )
      })

      const isFormValid = computed(() => {
        const { OriginStation, destinationStation, departureDate, returnDate } =
          journeyStore
        const isValidDateString = (
          dateStr: string | null | undefined
        ): boolean => {
          if (!dateStr || typeof dateStr !== 'string') {
            return false
          }
          const regex = /^\d{4}-\d{2}-\d{2}$/
          if (!regex.test(dateStr)) {
            return false
          }
          const parsed = new Date(dateStr)
          return isValid(parsed)
        }
        const checks = {
          hasOrigin: !!OriginStation?.name,
          hasDestination: !!destinationStation?.name,
          differentStations: OriginStation?.name !== destinationStation?.name,
          validDepartureDate: isValidDateString(departureDate),
          validReturnDate: returnDate ? isValidDateString(returnDate) : true, 
        }
        const isValidForm = Object.values(checks).every(Boolean)
        if (!isValidForm) {
          console.warn('Form validation failed:', checks)
        }
        return isValidForm
      })

      const handleSearch = async () => {
        if (!isFormValid.value) {
          console.warn('Form is invalid:', {
            OriginStation: journeyStore.OriginStation,
            destinationStation: journeyStore.destinationStation,
            departureDate: journeyStore.departureDate,
            returnDate: journeyStore.returnDate,
          })
          isAutoSubmitting.value = false
          return
        }
        try {
          await router.push({
            path: '/reiseplanlegger/reisende',
            query: route.query,
          })
        } catch (error) {
          console.error(
            'Navigation to /reiseplanlegger/reisende failed:',
            error
          )
          isAutoSubmitting.value = false
        }
      }

      const toLink = computed(() => {
        if (canProceed.value) {
          return { path: '/reiseplanlegger/reisende', query: route.query }
        }
        return '#'
      })

      watch(
        [
          () => journeyStore.OriginStation,
          () => journeyStore.destinationStation,
          () => journeyStore.departureDate,
          () => journeyStore.returnDate,
        ],
        () => {
          if (!isWatching.value) {
            return
          }
          const query = {
            origin: journeyStore.OriginStation?._u_i_c_station_code,
            destination: journeyStore.destinationStation?._u_i_c_station_code,
            outboundDate: journeyStore.departureDate,
            returnDate: journeyStore.returnDate || undefined,
            fromLight: route.query.fromLight ? 'true' : undefined,
          }
          Object.keys(query).forEach(
            (key) => {
              const k = key as keyof typeof query
              if (query[k] === undefined) {
                delete query[k]
              }
            }
          )
          router.replace({ query })
        },
        { deep: true }
      )

      const removeLocalStorageData = () => {
        localStorage.removeItem("pendingPayment");
        localStorage.removeItem("paymentFormData");
        localStorage.removeItem("pendingMyTravelState");
        localStorage.removeItem("hasAttemptedPaymentBefore"); 
      } 

      onMounted(async () => {
        try {
          const {
            origin,
            destination,
            outboundDate,
            departureDate,
            returnDate,
            fromLight,
          } = route.query
          const isFromLightVersion = fromLight === 'true'
          if (isFromLightVersion) {
            isAutoSubmitting.value = true
            isWatching.value = false
          }

          // Verify and restore CRM token if user is logged in
          if (
            userStore.isLoggedIn &&
            (!userStore.token ||
              !userStore.tokenExpirationTime ||
              Date.now() >= userStore.tokenExpirationTime)
          ) {
            try {
              await getToken(false)
            } catch (error) {
              console.error('Failed to restore CRM token:', error)
              userStore.logout()
            }
          }

          try {
            const storedBookingNumber = getStoredProvisionalBookingNumber();
            
            if (storedBookingNumber && (!bookingStore.bookingData || bookingStore.bookingNumber !== storedBookingNumber)) {
              try {
                const errorStore = useErrorStore();
                bookingStore.setBookingNumber(storedBookingNumber);
                
                try {
                  await bookingStore.fetchBookingData(storedBookingNumber);
                } catch (fetchError) {
                  console.error('Failed to fetch booking data for stored booking:', fetchError);
                  errorStore.clearError();
                }
              } catch (error) {
                console.error('Error in booking fetch process:', error);
              }
            }
            
            // if (bookingStore.bookingData) {
            //   if (!bookingStore.bookingData.confirmed_timestamp) {
            //     try {
            //       await cancelProvisionalBooking(bookingStore.bookingNumber as string, true);
            //     } catch (cancelError) {
            //       console.log('Silent error cancelling booking:', cancelError);
            //     }
            //   }
            // }
            
            if (storedBookingNumber) {
              clearProvisionalBookingInfo();
            }
            
          } catch (error) {
            console.error('Error in booking cancellation handling:', error);
            try {
              clearProvisionalBookingInfo();
            } catch {}
          }
          
          bookingStore.$reset()
          seatReservationStore.$reset()
          myTravelStore.reset()
          journeyStore.clearDiscountCards()
          bookingStore.setIsRebooking(false)
          removeLocalStorageData()

          if (!journeyStore.allStations.length) {
            try {
              const stations = await fetchAllStations()
              journeyStore.setAllStations(stations)
            } catch (error) {
              console.error('Failed to fetch stations:', error)
              isAutoSubmitting.value = false
              isWatching.value = true
              return
            }
          }

          if (origin && typeof origin === 'string') {
            const station = journeyStore.allStations.find(
              (s) => s.name === origin || s._u_i_c_station_code === origin
            )
            if (station) {
              journeyStore.setOriginStation(station)
            } else {
              console.warn(
                `Origin station with name or UIC code ${origin} not found`
              )
            }
          }

          if (destination && typeof destination === 'string') {
            const station = journeyStore.allStations.find(
              (s) =>
                s.name === destination || s._u_i_c_station_code === destination
            )
            if (station) {
              journeyStore.setDestinationStation(station)
            } else {
              console.warn(
                `Destination station with name or UIC code ${destination} not found`
              )
            }
          }

          const date = outboundDate || departureDate
          if (date && typeof date === 'string') {
            journeyStore.setDepartureDate(date)
          } else {
            console.warn('No valid departure date provided')
          }

          if (typeof returnDate === 'string' && returnDate !== '') {
            journeyStore.setReturnDate(returnDate)
          } else {
            journeyStore.setReturnDate(null) 
          }

          if (journeyStore.selectedSeats.length > 0) {
            journeyStore.selectedSeats = []
          }
          if (journeyStore.selectedProducts.length > 0) {
            journeyStore.selectedProducts = []
          }
          if (bookingStore.selectedEquipment.outbound.length > 0) {
            bookingStore.selectedEquipment.outbound = []
          }
          if (bookingStore.selectedEquipment.inbound.length > 0) {
            bookingStore.selectedEquipment.inbound = []
          }
          seatReservationStore.clearSeats()

          if (isFromLightVersion && isFormValid.value) {
            await handleSearch()
            await nextTick()
            isWatching.value = true
          } else if (isFromLightVersion) {
            console.warn('Auto-submit skipped due to invalid form data:', {
              OriginStation: journeyStore.OriginStation,
              destinationStation: journeyStore.destinationStation,
              departureDate: journeyStore.departureDate,
              returnDate: journeyStore.returnDate,
            })
            isAutoSubmitting.value = false
            isWatching.value = true
          }
        } catch (error) {
          console.error('Error initializing SearchPage:', error)
          isAutoSubmitting.value = false
          isWatching.value = true
        }
      })

      return {
        canProceed,
        isAutoSubmitting,
        toLink,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .search-page {
    position: relative;
    margin: 0 auto;
    width: 100%;
    max-width: 306px;
    background-color: $clr-white;
    padding: 24px;
    color: #333;
    box-shadow: $box-shadow-card;
    border-radius: $border-radius;
    border: 1px solid lightgray;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 12px;

    @media (min-width: $breakpoint-sm) {
      max-width: 360px;
    }

    &__bg-image {
      position: absolute;
      right: 0;
      left: 0;
      min-height: 400px;
      padding: rem(20) 16px rem(180);
      background: $clr-white url(@/assets/icons/landscape.svg) center 100%
        no-repeat;
      background-position: left bottom;
      background-size: 80vw;

      @media (min-width: $breakpoint-md) {
        min-height: 480px;
        padding-bottom: rem(20);
        background-size: 50vw;
      }
    }

    &__headline {
      position: relative;
      z-index: 1;
      width: 100%;
      max-width: 1168px;
      margin: 0 auto;
    }

    &__wrapper {
      position: relative;
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      background-color: $clr-white;
    }

    &__inner {
      position: relative;
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 32px;
      min-height: 350px;
      padding: 20px 16px;
    }

    & > * {
      flex: 1 1 auto;
    }

    @media (min-width: $breakpoint-xl) {
      flex-direction: row;
      align-items: center;
      max-width: 1120px;
    }

    .search-container {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 12px;

      & > * {
        flex: 1 1 auto;
      }

      @media (min-width: $breakpoint-xl) {
        flex-direction: row;
      }
    }

    &__calendar-mobile {
      display: flex;
      flex-direction: row;
      align-items: stretch;
      flex: 1 1 auto;
      gap: 12px;
      & > * {
        flex: 1;
      }
      @media (min-width: $breakpoint-xl) {
        display: none;
      }
    }

    &__calendar-desktop {
      display: none;
      @media (min-width: $breakpoint-xl) {
        display: flex;
        flex-direction: row;
        align-items: stretch;
        gap: 16px;
        & > * {
          flex: 1;
        }
      }
    }

    &__discount {
      text-align: center;
      padding: 15px;
    }

    .button {
      padding: rem(20) 40px;
    }
    .button.disabled {
      @include button-disabled;
    }
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;

    &.search-page {
      > * {
        flex: auto;
      }
    }
  }
</style>
